import { isEqual, sortBy, uniqueId } from 'lodash'

export type GroupApplicationFormType = 'waiver' | 'identification' | 'questionnaire'

export interface GroupApplicationFormRow {
  itemId: string
  type: GroupApplicationFormType
  id?: number
  name?: string
  required: boolean
  surveyId?: number
}

export interface GroupApplicationFormValues {
  useDefaultStartingInstructions: boolean
  applicationStartInstructions: string
  useDefaultCompletionInstructions: boolean
  applicationCompletionInstructions: string
  forms: GroupApplicationFormRow[]
}

interface BuildFormRowsOptions {
  credentials: any[]
  questionSets: any[]
  isQuestionnaireAsSurveyEnabled: boolean
}

const getCredentialName = (credential: any) => credential.name || credential.version?.title || ''

export const buildGroupApplicationFormRows = ({
  credentials,
  questionSets,
  isQuestionnaireAsSurveyEnabled,
}: BuildFormRowsOptions): GroupApplicationFormRow[] => {
  const credentialRows: Array<GroupApplicationFormRow & { displayOrder: number }> = credentials
    .filter(item => ['waiver', 'identification'].includes(item.type))
    .map(item => ({
      itemId: uniqueId('credential-'),
      type: item.type as 'waiver' | 'identification',
      id: item.id,
      name: getCredentialName(item),
      required: item.required ?? false,
      displayOrder: item.displayOrder ?? 0,
    }))

  const questionRows: Array<GroupApplicationFormRow & { displayOrder: number }> = questionSets.map(item => ({
    itemId: uniqueId('questionnaire-'),
    type: 'questionnaire',
    id: isQuestionnaireAsSurveyEnabled ? item.survey?.id : item.questionSet?.id,
    name: item.questionSet?.name,
    required: item.required ?? false,
    surveyId: isQuestionnaireAsSurveyEnabled ? item.survey?.id : undefined,
    displayOrder: item.displayOrder ?? 0,
  }))

  return sortBy([...credentialRows, ...questionRows], 'displayOrder').map(({ displayOrder, ...row }) => row)
}

export const buildGroupApplicationInitialValues = (
  group: {
    applicationStartInstructions?: string | null
    applicationCompletionInstructions?: string | null
  },
  options: BuildFormRowsOptions,
): GroupApplicationFormValues => ({
  useDefaultStartingInstructions: !group.applicationStartInstructions,
  applicationStartInstructions: group.applicationStartInstructions || '',
  useDefaultCompletionInstructions: !group.applicationCompletionInstructions,
  applicationCompletionInstructions: group.applicationCompletionInstructions || '',
  forms: buildGroupApplicationFormRows(options),
})

const normalizeForms = (forms: GroupApplicationFormRow[]) =>
  forms.map(({ itemId, ...form }, index) => ({
    ...form,
    displayOrder: index,
  }))

export const areGroupApplicationInstructionsDirty = (
  values: GroupApplicationFormValues,
  initialValues: GroupApplicationFormValues,
) => {
  if (values.useDefaultStartingInstructions !== initialValues.useDefaultStartingInstructions) {
    return true
  }

  if (values.useDefaultCompletionInstructions !== initialValues.useDefaultCompletionInstructions) {
    return true
  }

  if (
    !values.useDefaultStartingInstructions &&
    values.applicationStartInstructions !== initialValues.applicationStartInstructions
  ) {
    return true
  }

  if (
    !values.useDefaultCompletionInstructions &&
    values.applicationCompletionInstructions !== initialValues.applicationCompletionInstructions
  ) {
    return true
  }

  return false
}

export const areGroupApplicationFormsDirty = (
  forms: GroupApplicationFormRow[],
  initialForms: GroupApplicationFormRow[],
) => !isEqual(normalizeForms(forms), normalizeForms(initialForms))

export const areGroupApplicationValuesDirty = (
  values: GroupApplicationFormValues,
  initialValues: GroupApplicationFormValues,
) =>
  areGroupApplicationInstructionsDirty(values, initialValues) ||
  areGroupApplicationFormsDirty(values.forms, initialValues.forms)

export const buildGroupApplicationInstructionsUpdatePayload = (
  values: GroupApplicationFormValues,
  initialValues: GroupApplicationFormValues,
) => {
  const payload: {
    applicationStartInstructions?: string | null
    applicationCompletionInstructions?: string | null
  } = {}

  const startDirty =
    values.useDefaultStartingInstructions !== initialValues.useDefaultStartingInstructions ||
    (!values.useDefaultStartingInstructions &&
      values.applicationStartInstructions !== initialValues.applicationStartInstructions)

  const completionDirty =
    values.useDefaultCompletionInstructions !== initialValues.useDefaultCompletionInstructions ||
    (!values.useDefaultCompletionInstructions &&
      values.applicationCompletionInstructions !== initialValues.applicationCompletionInstructions)

  if (startDirty) {
    payload.applicationStartInstructions = values.useDefaultStartingInstructions
      ? ''
      : values.applicationStartInstructions || ''
  }

  if (completionDirty) {
    payload.applicationCompletionInstructions = values.useDefaultCompletionInstructions
      ? ''
      : values.applicationCompletionInstructions || ''
  }

  return payload
}

export const serializeGroupApplicationForms = (
  forms: GroupApplicationFormRow[],
  isQuestionnaireAsSurveyEnabled: boolean,
) => {
  const formsWithOrder = forms.map((form, index) => ({ ...form, displayOrder: index }))

  return {
    credentials: formsWithOrder
      .filter(form => (form.type === 'waiver' || form.type === 'identification') && form.id)
      .map(({ id, required, displayOrder }) => ({
        id: id as number,
        required,
        displayOrder,
      })),
    questionSets: formsWithOrder
      .filter(form => form.type === 'questionnaire' && form.id)
      .map(({ id, required, displayOrder, surveyId }) =>
        isQuestionnaireAsSurveyEnabled
          ? { surveyId: surveyId ?? id, required, displayOrder }
          : { questionSetId: id, required, displayOrder },
      ),
  }
}

export const areGroupApplicationCredentialsDirty = (
  forms: GroupApplicationFormRow[],
  initialForms: GroupApplicationFormRow[],
  isQuestionnaireAsSurveyEnabled: boolean,
) => {
  const current = serializeGroupApplicationForms(forms, isQuestionnaireAsSurveyEnabled).credentials
  const initial = serializeGroupApplicationForms(initialForms, isQuestionnaireAsSurveyEnabled).credentials

  return !isEqual(current, initial)
}

export const areGroupApplicationQuestionSetsDirty = (
  forms: GroupApplicationFormRow[],
  initialForms: GroupApplicationFormRow[],
  isQuestionnaireAsSurveyEnabled: boolean,
) => {
  const current = serializeGroupApplicationForms(forms, isQuestionnaireAsSurveyEnabled).questionSets
  const initial = serializeGroupApplicationForms(initialForms, isQuestionnaireAsSurveyEnabled).questionSets

  return !isEqual(current, initial)
}

export const getGroupApplicationFormDisplayName = (
  form: Pick<GroupApplicationFormRow, 'type' | 'id' | 'name'>,
  options: {
    waiverOptions: Array<{ id: number; name: string }>
    identificationOptions: Array<{ id: number; name: string }>
    questionnaireOptions: Array<{ id: number; name: string }>
  },
) => {
  if (form.name) {
    return form.name
  }

  if (!form.id) {
    return ''
  }

  if (form.type === 'waiver') {
    return options.waiverOptions.find(option => option.id === form.id)?.name || ''
  }

  if (form.type === 'identification') {
    return options.identificationOptions.find(option => option.id === form.id)?.name || ''
  }

  return options.questionnaireOptions.find(option => option.id === form.id)?.name || ''
}

export const hasIncompleteGroupApplicationForms = (forms: GroupApplicationFormRow[]) => forms.some(form => !form.id)
